import { createFileRoute, Link } from "@tanstack/react-router";
import heroPms from "@/assets/hero/hero-property-management-software.jpg.asset.json";

const HERO = {
  src: heroPms.url,
  alt: "Property manager at a tidy office desk reviewing a rent-roll dashboard on a laptop, with a multi-unit rental community visible through the window",
};
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

export const Route = createFileRoute("/business-setup/property-management-software")({
  head: () =>
    buildHead({
      title: "Property Management Software for Landlords | Simply Approved",
      description:
        "Rent collection, lease tracking, maintenance, and owner reporting software for real estate investors — the records underwriting asks for on portfolio files.",
      url: "/business-setup/property-management-software",
      type: "article",
      keywords:
        "property management software for landlords, rent collection software investors, rent roll software portfolio loan, lease tracking software rental property, owner reporting software landlord, DSCR rent roll documentation, online rent collection LLC, maintenance tracking rental property, portfolio loan documentation software, expense tracking rental property, refinance rent roll software",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Property management software", url: "/business-setup/property-management-software" },
      ],
      service: { name: "Property Management Software Referral" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: PmsPage,
});

function PmsPage() {
  const partner = partnerBySlug("doorloop")!;
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Property management software for your portfolio"
        subtitle="Clean rent rolls, leases, and expense records make refinances and portfolio loans move faster — and make your returns easier to see."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"property management software"}
        intro={"When you refinance, pull cash out, or roll several doors into a portfolio loan, the file comes down to documentation: leases, rent rolls, and proof rent is actually being collected. Investors tracking this in a spreadsheet spend weeks rebuilding it. Software produces the same reports in a few clicks."}
        reasons={[
          { title: "Rent rolls and leases on demand", body: "Portfolio and cash-out files ask for a current rent roll and executed leases per unit. Having them exportable saves real time." },
          { title: "Proof of collected rent", body: "Deposit history tied to each lease supports the rental income used in DSCR underwriting." },
          { title: "Maintenance and expense records", body: "Clean expense tracking makes your CPA's job easier and gives you a true picture of net cash flow per property." },
          { title: "It scales past three or four doors", body: "Manual tracking breaks down as the portfolio grows. Setting the system up early is much easier than migrating later." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="What it helps with" title="Records investors are asked for" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Current rent roll across every door you own",
            "Executed leases with term dates and rent amounts",
            "Rent receipt history for seasoning and DSCR support",
            "Operating expense tracking by property",
            "Maintenance and turnover history",
            "Owner statements and year-end reporting for your accountant",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Add your properties, units, and current leases.",
            "Turn on online rent collection so payments and deposits are recorded automatically.",
            "Export the rent roll and owner statements when you apply for your next loan.",
          ]}
        />
        <PartnerCTA partner={partner} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/portfolio-rental-loans" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Portfolio rental loans</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
